/**
 * Ingressi — unified inbound sources page.
 *
 * Replaces the legacy "Endpoint webhook" page: webhooks are now just
 * one of several ingress types (sftp_pull, file_watch, db_poll,
 * http_cron_pull). The ?type= query param pre-filters by adapter type
 * so the old /webhooks?... redirect from the sidebar still
 * lands users where they expect.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Inbox, Pause, Pencil, Play, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { IngressDetailDrawer } from "@/components/ingress/IngressDetailDrawer";
import { IngressSourceDialog, type DialogMode } from "@/components/ingress/IngressSourceDialog";
import { TagFilter, TagList } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { ingressApi, type IngressSource, type IngressType } from "@/lib/api-ingress";
import { usePaginationState } from "@/lib/pagination";
import { usePermissions } from "@/lib/permissions";

const TYPE_TONE: Record<IngressType, "neutral" | "active" | "warning"> = {
  webhook: "neutral",
  sftp_pull: "active",
  file_watch: "active",
  db_poll: "active",
  http_cron_pull: "active",
};

export function IngressPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  const canWrite = can("ingress", "write");
  const canDelete = can("ingress", "delete");
  const typeFilter = (searchParams.get("type") || "") as IngressType | "";

  const [dialog, setDialog] = React.useState<{ open: boolean; mode: DialogMode }>({
    open: false,
    mode: { kind: "create" },
  });
  const [drawerSource, setDrawerSource] = React.useState<IngressSource | null>(null);
  const [tagFilter, setTagFilter] = React.useState<string[]>([]);

  const { page, pageSize, setPage, setPageSize, reset } = usePaginationState();
  const query = useQuery({
    queryKey: ["ingress", typeFilter, page, pageSize, tagFilter],
    queryFn: () =>
      ingressApi.list({
        page,
        page_size: pageSize,
        type: typeFilter || undefined,
        tag: tagFilter.length > 0 ? tagFilter : undefined,
      }),
    refetchInterval: 10_000,
  });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const toggleMutation = useMutation({
    mutationFn: (args: { id: string; enabled: boolean }) =>
      args.enabled ? ingressApi.resume(args.id) : ingressApi.pause(args.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingress"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingressApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingress"] }),
  });

  function setType(next: string) {
    if (!next) {
      searchParams.delete("type");
    } else {
      searchParams.set("type", next);
    }
    setSearchParams(searchParams, { replace: true });
    reset();
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("ingress.title", "Ingressi")}
        description={t(
          "ingress.subtitle",
          "Sorgenti in ingresso unificate: webhook, SFTP, file watch, polling DB, fetch HTTP via cron.",
        )}
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setDialog({ open: true, mode: { kind: "create" } })}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("ingress.new", "New source")}
            </Button>
          )
        }
      />
      <PageContent>
        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
          <span className="text-muted-foreground">Type</span>
          <select
            className="border border-border bg-background px-2 py-1"
            value={typeFilter}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">all</option>
            <option value="webhook">webhook</option>
            <option value="sftp_pull">sftp_pull</option>
            <option value="file_watch">file_watch</option>
            <option value="db_poll">db_poll</option>
            <option value="http_cron_pull">http_cron_pull</option>
          </select>
          <TagFilter
            value={tagFilter}
            onChange={(next) => {
              setTagFilter(next);
              reset();
            }}
            scope="ingress_sources"
          />
        </div>

        {(typeFilter === "" || typeFilter === "webhook") && (
          <Alert tone="info" title={t("ingress.webhookHint", "Webhook URL")} className="mb-4">
            Webhook rows are receivable at{" "}
            <span className="font-mono">POST /api/webhooks/&lt;name&gt;</span>. All other types are
            driven by the scheduler or a long-lived listener — no public URL.
          </Alert>
        )}

        {items.length > 0 ? (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-32">Type</Th>
                  <Th>Name</Th>
                  <Th>Format</Th>
                  <Th>Tags</Th>
                  <Th>Last poll</Th>
                  <Th>Last success</Th>
                  <Th className="w-20">Enabled</Th>
                  {(canWrite || canDelete) && (
                    <Th className="w-32 text-right">Actions</Th>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {items.map((e) => (
                  <Tr
                    key={e.id}
                    onClick={() => setDrawerSource(e)}
                    className="cursor-pointer hover:bg-accent/5"
                  >
                    <Td>
                      <Badge tone={TYPE_TONE[e.type]}>{e.type}</Badge>
                    </Td>
                    <Td className="font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <Inbox
                          className="h-3.5 w-3.5 text-muted-foreground"
                          strokeWidth={1.5}
                        />
                        {e.name}
                        <DescriptionTooltip text={e.description} />
                      </div>
                      {e.type === "webhook" && (
                        <button
                          type="button"
                          className="mt-0.5 inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            navigator.clipboard.writeText(`/api/webhooks/${e.name}`);
                          }}
                          title="Copy URL"
                        >
                          /api/webhooks/{e.name}
                          <Copy className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                      )}
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">
                      {e.format?.type ?? "auto"}
                    </Td>
                    <Td>
                      <TagList tags={e.tags} max={3} />
                    </Td>
                    <Td className="text-xs text-muted-foreground">{fmt(e.last_poll_at)}</Td>
                    <Td className="text-xs text-muted-foreground">{fmt(e.last_success_at)}</Td>
                    <Td onClick={(ev) => ev.stopPropagation()}>
                      {canWrite ? (
                        <Switch
                          checked={e.enabled}
                          onCheckedChange={(v) =>
                            toggleMutation.mutate({ id: e.id, enabled: v })
                          }
                        />
                      ) : (
                        <Badge tone={e.enabled ? "active" : "deprecated"}>
                          {e.enabled ? "on" : "off"}
                        </Badge>
                      )}
                    </Td>
                    {(canWrite || canDelete) && (
                      <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {canWrite && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={e.enabled ? "Pause" : "Resume"}
                              onClick={() =>
                                toggleMutation.mutate({ id: e.id, enabled: !e.enabled })
                              }
                            >
                              {e.enabled ? (
                                <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />
                              ) : (
                                <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
                              )}
                            </Button>
                          )}
                          {canWrite && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Edit"
                              onClick={() =>
                                setDialog({
                                  open: true,
                                  mode: { kind: "edit", source: e },
                                })
                              }
                            >
                              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Delete"
                              onClick={() => {
                                if (confirm(`Delete ingress source ${e.name}?`)) {
                                  deleteMutation.mutate(e.id);
                                }
                              }}
                            >
                              <Trash2
                                className="h-3.5 w-3.5 text-danger"
                                strokeWidth={1.5}
                              />
                            </Button>
                          )}
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        ) : query.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <EmptyState
            icon={Inbox}
            title={t("ingress.empty", "No ingress sources yet")}
            description={t(
              "ingress.emptyDescription",
              "Register webhooks, SFTP pulls, file watches, DB polls or HTTP cron fetches — all through the same UI.",
            )}
            action={
              canWrite && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDialog({ open: true, mode: { kind: "create" } })}
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Register first source
                </Button>
              )
            }
          />
        )}
      </PageContent>

      <IngressSourceDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      />
      <IngressDetailDrawer
        source={drawerSource}
        onOpenChange={(o) => !o && setDrawerSource(null)}
        onEdit={(s) => {
          setDrawerSource(null);
          setDialog({ open: true, mode: { kind: "edit", source: s } });
        }}
      />
    </PageContainer>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}
